import { useEffect, useMemo } from 'react';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { SequenceInstanceWrapper } from './style';
import useGetSequenceInstance from 'hooks/services/strategy/useGetSequenceInstance';
import { useConditionalSnackBar } from 'context/snackbar';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { colors } from 'crono-fe-common/theme';
import { getCurrentSituationTags, getError } from 'crono-fe-common/utils';
import { ReactComponent as NotOwnedIcon } from 'crono-fe-common/icons/Not-owned-by-the-user.svg';
import { ReactComponent as CiruclarArrowsIcon } from 'assets/images/Icon-circular-arrows.svg';
import useStopSequenceInstance from 'hooks/services/strategy/useStopSequenceInstance';
import { Prospect } from 'crono-fe-common/types/prospect';
import CloseTabButton from 'crono-fe-common/components/CronoButton/closeTabButton';
import CronoStatus from 'components/CronoStatus';
import SequenceInstanceContent from './sequenceInstanceContent';
import { useConfirmModal } from 'context/confirmModal';
import useRestartStrategy from 'hooks/services/strategy/useRestartStrategy';
import { useAnalytics } from 'context/analytics';
import TagsBoxShowAll from 'components/TagsBoxShowAll';
import IconRectangle from 'crono-fe-common/icons/Icon-Rectangle';
import TrashCanDeleteIcon from 'crono-fe-common/icons/Icon-Trash-Can';
import useDeleteSequenceInstance from 'hooks/services/strategy/useDeleteSequenceInstance';
import useGetStrategySettings from 'hooks/services/strategySettings/useGetStrategySettings';
import { RewriteInProgressStatuses } from 'crono-fe-common/types/enums/rewriteStatus';
import { useInvalidateGetStrategy } from 'hooks/services/strategy/useGetStrategy';
import { StrategyCopyConstants } from 'crono-fe-common/constants/copyConstants/strategyCopyConstants';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useNonOwnedSequenceActionGuard from 'hooks/strategy/useNonOwnedSequenceActionGuard';
import useOnBehalfEditConfirm from 'hooks/strategy/useOnBehalfEditConfirm';

const REWRITE_STATUS_POLL_INTERVAL = 10_000;

interface IProps {
  //The possibilities are to pass sequenceInstanceId with prospect or to pass the task, depending of what it's possible to have
  sequenceInstanceId?: number;
  prospect?: Prospect;
  task?: TaskTodo;
  onClose: () => void;
  handleOpenDateCalendar: (task: TaskTodo) => void;
}

const SequenceInstanceDisplay = ({
  sequenceInstanceId,
  prospect,
  task,
  onClose,
  handleOpenDateCalendar,
}: IProps) => {
  const { openModal: openConfirmModal } = useConfirmModal();
  const { isManager } = useRoleCapabilities();
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();
  const sequenceInstanceIdFinal = useMemo(
    () =>
      sequenceInstanceId ? sequenceInstanceId : (task?.sequenceInstanceId ?? 0),
    [sequenceInstanceId, task],
  );
  const prospectIdFinal = useMemo(
    () => (prospect ? prospect.objectId : (task?.prospectId ?? '')),
    [prospect, task],
  );
  const { data: sequenceInstance, isError: errorGetSequenceInstance } =
    useGetSequenceInstance(
      sequenceInstanceIdFinal,
      prospectIdFinal,
      true,
      (data) => {
        const rewriteStatus = data?.data?.data?.rewriteStatus;
        if (
          rewriteStatus &&
          RewriteInProgressStatuses.includes(rewriteStatus)
        ) {
          return REWRITE_STATUS_POLL_INTERVAL;
        }
        return false;
      },
      //The prospect prop is only resolved from the task below, after this call
      !(prospect ?? task?.prospect)?.owned,
    );

  const sequenceInstanceData = sequenceInstance?.data?.data;
  const strategyId = sequenceInstanceData?.strategyId;
  const { data: strategySettingsResponse } = useGetStrategySettings(strategyId);
  const strategySettings = strategySettingsResponse?.data?.data;

  const analytics = useAnalytics();

  if (!prospect) {
    prospect = task?.prospect;
  }

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const {
    mutate: stopSequenceInstance,
    isSuccess: isStopSequenceInstanceSuccess,
    error: errorStopSequenceInstance,
  } = useStopSequenceInstance();

  const {
    mutate: restartStrategy,
    isSuccess: isRestartStrategySuccess,
    error: errorRestartStrategy,
  } = useRestartStrategy();

  //Managers can stop or restart the sequence of a contact they don't own,
  //without taking ownership. Removing the contact has no on-behalf support
  const isNotOwned = !prospect?.owned;
  const canActOnSequence = !isNotOwned || isManager;
  //The flag is only accepted from a manager, so a non-manager reaching an
  //unowned sequence through a task of their own must not send it
  const canActOnBehalf = isNotOwned && isManager;
  /**
   * Not every opener puts the sequence instance on the prospect — the drawer
   * timeline's task carries only its id — so the fetched instance is what the
   * actions read, falling back to the prospect's copy while it loads.
   */
  const currentSequenceInstance =
    sequenceInstance?.data?.data ?? prospect?.sequenceInstance;
  const isSequenceActive = !!currentSequenceInstance?.active;

  const { confirmEdit } = useOnBehalfEditConfirm(
    currentSequenceInstance?.id,
    canActOnBehalf,
  );

  const handleStopSequence = () => {
    const currentProspect = prospect;

    if (!currentProspect || !currentSequenceInstance || !canActOnSequence) {
      return;
    }

    const stopSequence = () => {
      stopSequenceInstance({
        sequenceInstanceId: currentSequenceInstance.id,
        prospectId: currentProspect.objectId,
        strategyId: currentSequenceInstance.strategyId,
        onBehalf: canActOnBehalf,
      });
      if (analytics) {
        analytics.track('stop-sequence', {});
      }
    };

    if (isNotOwned) {
      openNonOwnedSequenceActionModal('stop', stopSequence);
      return;
    }

    stopSequence();
  };

  useEffect(() => {
    if (isStopSequenceInstanceSuccess) {
      onClose();
      invalidateGetStrategy({ strategyId });
    }
  }, [isStopSequenceInstanceSuccess]);

  useEffect(() => {
    if (isRestartStrategySuccess) {
      onClose();
    }
  }, [isRestartStrategySuccess]);

  const handleRestartStrategy = () => {
    if (!canActOnSequence) return;

    const currentProspect = prospect;

    const restartSequence = () => {
      if (!strategyId || !currentProspect || !strategySettings) return;
      restartStrategy({
        data: { prospectId: currentProspect.objectId, strategyId: strategyId },
        recipient: currentProspect,
        strategySettings: strategySettings,
      });
      if (analytics) {
        analytics.track('sequence-restarted', {
          prospectId: currentProspect.objectId,
          strategyId: sequenceInstanceData?.strategyId,
        });
      }
    };

    if (isNotOwned) {
      openNonOwnedSequenceActionModal('restart', restartSequence);
      return;
    }

    openConfirmModal({
      title: 'Are you sure you want to restart this sequence?',
      text: 'If you restart the sequence you will lose all your tasks personalization',
      confirmText: 'Restart',
      cancelText: 'Cancel',
      confirmFunction: restartSequence,
      disableClickOnBackground: true,
    });
  };

  const {
    mutate: deleteSequenceInstance,
    isLoading: isDeleteSequenceInstanceLoading,
    isSuccess: isDeleteSequenceInstanceSuccess,
    error: errorDeleteSequenceInstance,
  } = useDeleteSequenceInstance();

  const handleRemoveFromStrategy = () => {
    if (!prospect || !currentSequenceInstance?.id || isNotOwned) return;
    deleteSequenceInstance({
      prospectId: prospect.objectId,
      sequenceInstanceId: currentSequenceInstance.id,
      strategyId: currentSequenceInstance.strategyId,
    });
    if (analytics) {
      analytics.track('remove-prospect-from-strategy', {});
    }
  };

  useEffect(() => {
    if (isDeleteSequenceInstanceSuccess) {
      onClose();
      invalidateGetStrategy({
        strategyId: currentSequenceInstance?.strategyId,
      });
    }
  }, [isDeleteSequenceInstanceSuccess]);

  useConditionalSnackBar([
    {
      condition: !!errorGetSequenceInstance,
      message: 'Error getting sequence instance',
      severity: 'error',
    },
    {
      condition: !!isStopSequenceInstanceSuccess,
      message: 'Sequence correctly stopped',
      severity: 'success',
    },
    {
      condition: !!errorStopSequenceInstance,
      message:
        getError(errorStopSequenceInstance) ??
        'Error stopping sequence for this contact',
      severity: 'error',
    },
    {
      condition: !!isRestartStrategySuccess,
      message: 'Sequence correctly restarted',
      severity: 'success',
    },
    {
      condition: !!errorRestartStrategy,
      message:
        getError(errorRestartStrategy) ??
        'Error restarting sequence for this contact',
      severity: 'error',
    },
    {
      condition: !!errorDeleteSequenceInstance,
      message:
        getError(errorDeleteSequenceInstance) ?? 'Error removing contact',
      severity: 'error',
    },
    {
      condition: !!isDeleteSequenceInstanceSuccess,
      message: 'Contact removed from sequence',
      severity: 'success',
    },
  ]);
  if (!sequenceInstanceData) return null;
  return (
    <SequenceInstanceWrapper>
      <div className="background-component" id="sequence-instance-modal">
        <div className="container">
          <CloseTabButton close={onClose} />
          <div className="sequence-instance-container">
            <Typography fontSize={24} fontWeight={700}>
              {sequenceInstanceData.name}
            </Typography>
            <div className="prospect-info-container">
              <div className="row">
                <div className="prospect-name-title-container">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={500} noWrap>
                      {prospect?.name}{' '}
                      <span style={{ color: colors.primaryDark }}>
                        at{' '}
                        {prospect?.account?.name ??
                          task?.account?.name ??
                          'N/A'}
                      </span>
                    </Typography>
                    <CronoStatus
                      currentActualStatus={prospect?.actualStatus ?? null}
                      currentExternalValues={prospect?.externalValues ?? []}
                      disabled={true}
                      type="prospect"
                      style={{ height: 24, paddingBlock: 4 }}
                    />
                  </div>

                  <Typography fontSize={14} color={colors.grey11} noWrap>
                    {prospect!.title}
                  </Typography>
                </div>

                <div className="right-side-sequence-handle">
                  <Typography
                    fontWeight={500}
                    fontSize={12}
                    color={colors.grey11}
                  >
                    Progress:{' '}
                    <span style={{ color: colors.black }}>
                      {sequenceInstanceData.actualStep}/
                    </span>
                    {sequenceInstanceData.numberOfSteps}
                  </Typography>

                  {isSequenceActive && canActOnSequence && (
                    <Tooltip
                      arrow
                      title={StrategyCopyConstants.STOP_INSTANCE_TEXT}
                      placement="top"
                      enterDelay={400}
                      enterNextDelay={400}
                    >
                      <div
                        className="stop-strategy-button"
                        onClick={handleStopSequence}
                      >
                        <IconRectangle
                          color={colors.orange}
                          className="rectangle-icon"
                        />
                        <Typography
                          fontSize={14}
                          lineHeight={'18px'}
                          fontWeight={500}
                          color={colors.orange}
                          maxWidth={300}
                          noWrap
                        >
                          {currentSequenceInstance?.name ??
                            StrategyCopyConstants.STOP_INSTANCE_TEXT}
                        </Typography>
                      </div>
                    </Tooltip>
                  )}
                  {isSequenceActive && canActOnSequence && (
                    <Tooltip
                      arrow
                      title="Restart sequence for this contact"
                      placement="top"
                      enterDelay={400}
                      enterNextDelay={400}
                      PopperProps={{ sx: { zIndex: '10001 !important' } }}
                    >
                      <div
                        className="restart-button"
                        onClick={handleRestartStrategy}
                      >
                        <CiruclarArrowsIcon />
                      </div>
                    </Tooltip>
                  )}
                  {isSequenceActive &&
                    !isNotOwned &&
                    (isDeleteSequenceInstanceLoading ? (
                      <CircularProgress size={30} />
                    ) : (
                      <Tooltip
                        arrow
                        placement="top"
                        enterDelay={400}
                        enterNextDelay={400}
                        PopperProps={{ sx: { zIndex: '10001 !important' } }}
                        title="Remove contact from sequence"
                        onClick={handleRemoveFromStrategy}
                      >
                        <IconButton className="remove-from-strategy-button">
                          <TrashCanDeleteIcon color={colors.white} />
                        </IconButton>
                      </Tooltip>
                    ))}
                  {isNotOwned && (
                    <Tooltip
                      arrow
                      title={`The owner is ${
                        prospect?.user ?? task?.prospect?.user ?? 'deleted user'
                      }`}
                      placement="top"
                    >
                      <NotOwnedIcon style={{ width: 26, height: 26 }} />
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="tags-container">
                  <TagsBoxShowAll
                    //since we are passing prospect + sequenceInstanceId or task
                    type={
                      prospect?.fromContact || task?.prospect?.fromContact
                        ? 'Prospect'
                        : 'Lead'
                    }
                    currentSituationTags={getCurrentSituationTags(
                      prospect?.externalValues,
                    )}
                    disableEdit={true}
                    blockIsEditable={true}
                    showRemovable={false}
                  />
                </div>
              </div>
            </div>
            <SequenceInstanceContent
              sequenceInstance={sequenceInstanceData}
              handleOpenDateCalendar={handleOpenDateCalendar}
              onClose={onClose}
              prospectId={prospect?.objectId}
              taskOrder={task?.order ?? undefined}
              onBehalf={canActOnBehalf}
              sequenceNotOwned={isNotOwned}
              confirmEdit={confirmEdit}
            />
          </div>
        </div>
      </div>
    </SequenceInstanceWrapper>
  );
};

export default SequenceInstanceDisplay;
